import * as React from 'react'

import { F0DatePicker } from '@factorialco/f0-react'
import type { DatePickerValue } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type ReportsForm from 'modules/employees/lib/forms/Reports'

export const DATERANGES = {
  current_month: 'current_month',
  current_year: 'current_year',
  custom: 'custom',
}
interface Props {
  form: ReportsForm
}

const DateRange = ({ form }: Props) => {
  const minDate = React.useMemo(() => moment().subtract(1, 'years').startOf('year'), [])

  const startDate = form.get('from')
  const endDate = form.get('to')

  const parseDateValue = React.useCallback((value?: string) => {
    if (!value) return undefined
    const uiDate = stringToDate(value, 'ui')
    if (uiDate.isValid()) return uiDate.toDate()
    const backendDate = stringToDate(value, 'backend')
    return backendDate.isValid() ? backendDate.toDate() : undefined
  }, [])

  const dateValue = React.useMemo(() => {
    const from = parseDateValue(startDate.value)
    const to = parseDateValue(endDate.value)

    if (!from || !to) return undefined

    return {
      granularity: 'range' as const,
      value: {
        from,
        to,
      },
    }
  }, [endDate.value, parseDateValue, startDate.value])

  const onChangeEvent = (value: DatePickerValue | undefined) => {
    if (!value?.value) {
      startDate.set(undefined)
      endDate.set(undefined)
      return
    }

    const fromFormatted = value.value.from ? i18nDateToString(value.value.from, 'ui') : undefined
    const toFormatted = value.value.to ? i18nDateToString(value.value.to, 'ui') : undefined

    startDate.set(fromFormatted)
    endDate.set(toFormatted)
  }

  return (
    <>
      <FormRow>
        <Select
          label={i18n.t('employees.reports.daterange')}
          field={form.get('daterange')}
          options={[
            {
              value: DATERANGES.current_month,
              label: i18n.t('employees.reports.dateranges.current_month'),
            },
            {
              value: DATERANGES.current_year,
              label: i18n.t('employees.reports.dateranges.current_year'),
            },
            {
              value: DATERANGES.custom,
              label: i18n.t('employees.reports.dateranges.custom'),
            },
          ]}
        />
      </FormRow>

      {form.get('daterange').value === 'custom' && (
        <FormRow>
          <F0DatePicker
            label={i18n.t('time_off.date_range.label')}
            onChange={onChangeEvent}
            size="sm"
            value={dateValue}
            minDate={minDate.toDate()}
          />
        </FormRow>
      )}
    </>
  )
}

export default observer(DateRange)
